import React, { useEffect, useRef, useState, useContext, useMemo } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, Form, Input, Table, message, Pagination, Modal, Select, Tag } from 'antd';
import { debounce } from 'lodash';
import { IoSearchOutline } from 'react-icons/io5';
import { IoIosArrowDown } from 'react-icons/io';
import moment from 'moment';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

import Context from '../../context/Context';
import { api } from '../../api/api';
import SuccessIcon from '../../assets/successicon.svg';
import config from "../../config";
import styles from './modifyUserInformation.module.scss';

const ModifyUserInformation = () => {
	const recaptchaRef = useRef(null);
	const [captchaToken, setCaptchaToken] = useState('');
	const [captchaError, setCaptchaError] = useState('');
	const { SSPFacilities } = useContext(Context);
	const [form] = Form.useForm();
	const RECAPTCHA_SITE_KEY = config.RECAPTCHA_SITE_KEY;

	const userDetails = useMemo(
		() => JSON.parse(localStorage.getItem('user-onboard-data')),
		[]
	);
	const isAdmin = userDetails?.isAdmin === 'Y';

	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [validating, setValidating] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [visibleNric, setVisibleNric] = useState({});
	const [validationStatus, setValidationStatus] = useState({
		ad: false,
		sso: false,
		clinical: false,
		success: false,
		message: '',
		hasValidated: false,
	});
	const [tableParams, setTableParams] = useState({
		pagination: {
			current: 1,
			pageSize: 10,
			showSizeChanger: true,
			showQuickJumper: true,
			total: 0,
		},
	});

	const userNameWatch = Form.useWatch('userName', form);
	const nhNameWatch = Form.useWatch('nhName', form);
	const newUserNameWatch = Form.useWatch('newUserName', form);
	const nricWatch = Form.useWatch('nric', form);
	const nhNameData = useMemo(() => {
		return userDetails?.nhName
			? userDetails.nhName
					.split(',')
					.map((item) => item.trim())
					.filter(Boolean)
			: [];
	}, [userDetails?.nhName]);

	const hasMultipleNH = nhNameData.length > 1;

	const nhOptions = useMemo(() => {
		if (isAdmin) {
			const uniqueMap = new Map();

			(SSPFacilities?.data || []).forEach((item) => {
				const nhName = item?.nhName?.trim();
				if (nhName && !uniqueMap.has(nhName)) {
					uniqueMap.set(nhName, {
						label: nhName,
						value: nhName,
					});
				}
			});

			return Array.from(uniqueMap.values()).sort((a, b) =>
				a.label.localeCompare(b.label)
			);
		}

		return nhNameData.map((item) => ({
			label: item,
			value: item,
		}));
	}, [isAdmin, SSPFacilities?.data, nhNameData]);

	const handleCaptchaChange = (token) => {
		setCaptchaToken(token || '');
		setCaptchaError('');
	};

	const handleCaptchaExpired = () => {
		setCaptchaToken('');
		setCaptchaError('reCAPTCHA expired. Please verify again.');
	};

	const resetCaptcha = () => {
		setCaptchaToken('');
		setCaptchaError('');
		recaptchaRef.current?.reset();
	};
	const resetValidationState = () => {
		setValidationStatus({
			ad: false,
			sso: false,
			clinical: false,
			success: false,
			message: '',
			hasValidated: false,
		});
	};

	useEffect(() => {
	resetValidationState();
	resetCaptcha();
	// eslint-disable-next-line
}, [userNameWatch, nhNameWatch,newUserNameWatch, nricWatch]);

	const toggleNric = (id) => {
		setVisibleNric((prev) => {
			const isNowVisible = !prev[id];

			if (isNowVisible) {
				setTimeout(() => {
					setVisibleNric((p) => ({
						...p,
						[id]: false,
					}));
				}, 10000);
			}

			return {
				...prev,
				[id]: isNowVisible,
			};
		});
	};

	const fetchModifyUserInfoList = async () => {
		setLoading(true);
		try {
			const { current, pageSize } = tableParams.pagination;

			const response = await api.get({
				path:
					userDetails?.isAdmin === 'Y'
						? `/modify-user-information/list`
						: `/modify-user-information/list?nhName=${encodeURIComponent(
								userDetails?.nhName || ''
						  )}`,
				params: {
					page: current,
					size: pageSize,
					searchText: searchText?.trim() || undefined,
				},
				service: 'useronboarding',
			});

			if (response.status === 200) {
				const list = response.data?.data || [];
				const total = response.data?.pagination?.totalData ?? list.length;

				setData(list);
				setTableParams((prev) => ({
					...prev,
					pagination: {
						...prev.pagination,
						total,
					},
				}));
			}
		} catch (error) {
			message.error('Failed to load modify user information list');
		} finally {
			setLoading(false);
		}
	};

	const handleTableChange = (pagination) => {
		setTableParams((prev) => ({
			...prev,
			pagination,
		}));
	};

	const handleSearch = (e) => {
		const value = e?.target?.value ?? '';

		if (value.length === 0 || value.length >= 3) {
			setSearchText(value);
			setTableParams((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					current: 1,
				},
			}));
		}
	};

	const debouncedSearch = useRef(debounce(handleSearch, 300)).current;

	useEffect(() => {
		fetchModifyUserInfoList();
		// eslint-disable-next-line
	}, [
		tableParams.pagination.current,
		tableParams.pagination.pageSize,
		searchText,
	]);

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
		// eslint-disable-next-line
	}, []);

	const parseValidationMessage = (text = '') => {
		const normalized = String(text || '');

		const ad =
			normalized.includes('AD: User is active') ||
			normalized.includes('AD: User Active');

		const sso =
			normalized.includes('SSO: User is active') ||
			normalized.includes('SSO: User Active');

		const clinical =
			normalized.includes('Clinical: User is active') ||
			normalized.includes('Clinical: User Active');

		return {
			ad,
			sso,
			clinical,
			success: ad && sso && clinical,
		};
	};

	const handleValidate = async () => {
	if (!captchaToken) {
		setCaptchaError('Please complete the reCAPTCHA verification before validation.');
		return;
	}

	try {
		const values = await form.validateFields(['nhName', 'userName']);
		setValidating(true);
		resetValidationState();

		const response = await api.get({
			path: `/validate-user-information`,
			params: {
				username: values.userName?.trim(),
				nhName: values.nhName?.trim(),
			},
			service: 'useronboarding',
		});

		const responseMessage =
			response?.data?.message ||
			response?.data ||
			'Validation response received';

		const parsed = parseValidationMessage(responseMessage);

		setValidationStatus({
			ad: parsed.ad,
			sso: parsed.sso,
			clinical: parsed.clinical,
			success: parsed.success,
			message: responseMessage,
			hasValidated: true,
		});

		if (parsed.success) {
			Modal.success({
				title: 'Validation Success',
				content: 'Validation successful. Submit is now enabled.',
				okText: 'Close',
			});
		} else {
			Modal.warning({
				title: 'Validation Completed With Issues',
				content: responseMessage,
				okText: 'Close',
			});
		}
	} catch (error) {
		if (error?.errorFields) return;

		const errMsg =
			error?.response?.data?.message ||
			error?.response?.data ||
			error?.message ||
			'Validation failed';

		setValidationStatus({
			ad: false,
			sso: false,
			clinical: false,
			success: false,
			message: errMsg,
			hasValidated: true,
		});

		Modal.error({
			title: 'Validation Failed',
			content: errMsg,
			okText: 'Close',
		});
	} finally {
		setValidating(false);
	}
};

	const handleSubmit = async (values) => {
	if (!validationStatus.success) {
		Modal.warning({
			title: 'Validation Required',
			content:
				'Please validate the user first. Submit is allowed only when AD, SSO and Clinical are all active.',
			okText: 'Close',
		});
		return;
	}

	if (!captchaToken) {
		setCaptchaError('Please complete the reCAPTCHA verification.');
		return;
	}

	setSubmitting(true);
	try {
		const payload = {
			nhName: values.nhName?.trim(),
			userName: values.userName?.trim(),
			newUserName: values.newUserName?.trim(),
			nric: values.nric?.trim(),
			createdBy: userDetails?.userId,
			modifiedBy: userDetails?.userId,
			recaptchaToken: captchaToken,
		};

		const response = await api.post({
			path: `/modify-user-information`,
			formdata: payload,
			service: 'useronboarding',
		});

		if (response?.status === 200) {
			message.success('User information modification request submitted successfully');
			form.resetFields();

			form.setFieldsValue({
				nhName: isAdmin ? undefined : hasMultipleNH ? undefined : nhNameData[0] || undefined,
			});

			resetValidationState();
			resetCaptcha();

			setTableParams((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					current: 1,
				},
			}));

			await fetchModifyUserInfoList();
		} else {
			Modal.error({
				title: 'Submission Failed',
				content: 'Failed to submit modify user information request',
				okText: 'Close',
			});
			resetCaptcha();
		}
	} catch (error) {
		const errMsg =
			error?.response?.data?.message ||
			error?.response?.data?.result?.message ||
			error?.message ||
			'Failed to submit modify user information request';

		Modal.error({
			title: 'Submission Failed',
			content: errMsg,
			okText: 'Close',
		});

		resetCaptcha();
	} finally {
		setSubmitting(false);
	}
};

	const columns = [
		{
			title: 'NH Name',
			dataIndex: 'nhName',
			key: 'nhName',
			width: 180,
		},
		{
			title: 'User Name',
			dataIndex: 'userName',
			key: 'userName',
			width: 180,
		},
		{
			title: 'New User Name',
			dataIndex: 'newUserName',
			key: 'newUserName',
			width: 180,
		},
		{
			title: 'NRIC / FIN',
			dataIndex: 'nric',
			key: 'nric',
			width: 180,
			render: (value, record) => {
				if (!value) return '-';

				const isVisible = visibleNric[record.id];
				const masked = value.replace(/.(?=.{4})/g, '*');

				return (
					<div className={styles.nricCell}>
						<span>{isVisible ? value : masked}</span>
						{isVisible ? (
							<EyeInvisibleOutlined
								className={styles.nricEyeIcon}
								onClick={() => toggleNric(record.id)}
							/>
						) : (
							<EyeOutlined
								className={styles.nricEyeIcon}
								onClick={() => toggleNric(record.id)}
							/>
						)}
					</div>
				);
			},
		},
		{
			title: 'Submitted By',
			dataIndex: 'createdBy',
			key: 'createdBy',
			width: 180,
		},
		{
			title: 'Submitted Date',
			dataIndex: 'createdDate',
			key: 'createdDate',
			width: 180,
			render: (value) => (value ? moment(value).format('DD-MMM-YYYY HH:mm') : '-'),
		},
		// {
		// 	title: 'Updated By',
		// 	dataIndex: 'modifiedBy',
		// 	key: 'modifiedBy',
		// 	width: 180,
		// },
		// {
		// 	title: 'Updated Date',
		// 	dataIndex: 'modifiedDate',
		// 	key: 'modifiedDate',
		// 	width: 180,
		// 	render: (value) => (value ? moment(value).format('DD-MMM-YYYY HH:mm') : '-'),
		// },
		{
			title: 'Remarks',
			dataIndex: 'remarks',
			key: 'remarks',
			width: 400,
			render: (value) => value || '-',
		},
	];

	const initialValues = {
		nhName: isAdmin ? undefined : hasMultipleNH ? undefined : nhNameData[0] || undefined,
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.topSection}>
				<div className={styles.left}>
					<p className={styles.title}>Modify User Information</p>
				</div>
			</div>

			<div className={styles.formCard}>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					className={styles.form}
					initialValues={initialValues}
				>
					<div className={styles.formGrid}>
						<Form.Item
							label="NH Name"
							name="nhName"
							rules={[
								{
									required: true,
									message: 'Please select NH Name',
								},
							]}
						>
							{isAdmin || hasMultipleNH ? (
								<Select
									placeholder="Select NH Name"
									options={nhOptions}
									showSearch
									allowClear
									optionFilterProp="label"
									suffixIcon={<IoIosArrowDown style={{ fontSize: '14px' }} />}
								/>
							) : (
								<Input disabled placeholder="NH Name" />
							)}
						</Form.Item>

						<Form.Item
							label="User Name"
							name="userName"
							rules={[
								{
									required: true,
									message: 'Please enter existing username',
								},
							]}
						>
							<Input placeholder="Enter existing username" />
						</Form.Item>

						<Form.Item
							label="New User Name"
							name="newUserName"
							rules={[
								{
									required: true,
									message: 'Please enter new username',
								},
							]}
						>
							<Input placeholder="Enter new username" />
						</Form.Item>

						<Form.Item
							label="NRIC / FIN"
							name="nric"
							rules={[
								{
									required: true,
									message: 'Please enter NRIC / FIN',
								},
							]}
						>
							<Input placeholder="Enter NRIC / FIN" />
						</Form.Item>
					</div>
					<div style={{ marginBottom: 16 }}>
						{RECAPTCHA_SITE_KEY ? (
							<ReCAPTCHA
								ref={recaptchaRef}
								sitekey={RECAPTCHA_SITE_KEY}
								onChange={handleCaptchaChange}
								onExpired={handleCaptchaExpired}
							/>
						) : (
							<div style={{ color: '#ff4d4f' }}>
								Missing RECAPTCHA_SITE_KEY configuration
							</div>
						)}

						{captchaError && (
							<div style={{ color: '#ff4d4f', marginTop: 8 }}>
								{captchaError}
							</div>
						)}
					</div>

                    <div className={styles.formActions}>
						<Button
							type="default"
							onClick={handleValidate}
							loading={validating}
							disabled={!captchaToken}
							className={`${styles.actionBtn} ${styles.validateBtn}`}
						>
							Validate
						</Button>

                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={submitting}
                            className={`${styles.actionBtn} ${styles.submitBtn}`}
                            disabled={!validationStatus.success || !captchaToken}
                        >
                            Submit
                        </Button>
                    </div>

					{validationStatus.hasValidated && (
						<div className={styles.validationSummary}>
							<div className={styles.validationTags}>
								<Tag color={validationStatus.ad ? 'success' : 'error'}>
									AD: {validationStatus.ad ? 'Active' : 'Not Active'}
								</Tag>
								<Tag color={validationStatus.sso ? 'success' : 'error'}>
									SSO: {validationStatus.sso ? 'Active' : 'Not Active'}
								</Tag>
								<Tag color={validationStatus.clinical ? 'success' : 'error'}>
									Clinical: {validationStatus.clinical ? 'Active' : 'Not Active'}
								</Tag>
							</div>
							<div className={styles.validationMessage}>
								{validationStatus.message}
							</div>
						</div>
					)}
				</Form>
			</div>

			<div className={styles.listHeader}>
				<div className={styles.listHeaderLeft}>
					<div className={styles.totalWrap}>
						<img
							className={styles.successIcon}
							src={SuccessIcon}
							alt="Success Icon"
						/>
						<p className={styles.totalText}>
							Total Records : {tableParams.pagination.total}
						</p>
					</div>
				</div>

				<div className={styles.listHeaderRight}>
					<Input
						prefix={<IoSearchOutline />}
						className={styles.searchInput}
						placeholder="Search by NH name, username, new username, NRIC or remarks"
						onPressEnter={handleSearch}
						onChange={debouncedSearch}
						allowClear
					/>
				</div>
			</div>

			<div className={styles.tableWrapper}>
				<div className={styles.horizontalFixed}>
					<div className={styles.verticalScroll}>
						<Table
							className={styles.table}
							rowKey={(record) => record.id}
							dataSource={data}
							columns={columns}
							loading={loading}
							pagination={false}
							tableLayout="auto"
							onChange={handleTableChange}
							rowClassName={(_, index) =>
								index % 2 === 0 ? styles.evenRow : styles.oddRow
							}
							onRow={() => ({
								className: styles.rowWrapper,
							})}
							scroll={{
								x: 'max-content',
								y: 450,
							}}
						/>
					</div>
				</div>

				<div className={styles.paginationWrapper}>
					<Pagination
						current={tableParams.pagination.current}
						pageSize={tableParams.pagination.pageSize}
						total={tableParams.pagination.total}
						showSizeChanger
						showQuickJumper
						hideOnSinglePage
						onChange={(page, pageSize) =>
							handleTableChange({ current: page, pageSize })
						}
					/>
				</div>
			</div>
		</div>
	);
};

export default ModifyUserInformation;